import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { APP_CODE, realEntityName, SystemRole } from '@pqrs/contracts';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ShellApp, type EnlaceProps, type SeccionNavegacion } from '@xiltrion/ui/bloques/shell-app';
import { Button } from '@xiltrion/ui/button';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { Kbd } from '@xiltrion/ui/kbd';
import { useSession } from './session';
import { useSupportInboxCount } from '@/features/support/api';
import { useMyDashboard } from '@/features/tasks/api';
import { useFailedEmailCount } from '@/features/emails/api';
import { useGlobalShortcuts } from './shortcuts';
import { apiGet } from '@/api/client';
import { publicBrandingQuery } from '@/api/branding';
import { GlobalSearch } from '@/features/search/GlobalSearch';
import { CommandPalette } from '@/features/search/CommandPalette';
import { NotificationsBell } from '@/features/notifications/NotificationsBell';
import { EmailFailuresNotice } from '@/features/emails/EmailFailuresNotice';
import { SetupNotice } from '@/features/setup/SetupNotice';
import { getThemeMode, setThemeMode, THEME_EVENT, type ThemeMode } from './theme';

interface NavItem { to: string; label: string; icon: NombreIcono; show?: (s: ReturnType<typeof useSession>) => boolean }

/** Main navigation: few top-level entries, no dozens of options. */
const NAV: NavItem[] = [
  { to: '/', label: 'Inicio', icon: 'mdi:view-dashboard-outline' },
  { to: '/pqrs', label: 'Solicitudes', icon: 'mdi:inbox-outline' },
  // Con los apoyos entre áreas: los que llegan a sus áreas como tareas y los que pidió en su pestaña.
  { to: '/tareas', label: 'Mis tareas', icon: 'mdi:check-circle-outline' },
  // Asignación y reportes: el Administrador y quien lidera un área (organigrama).
  { to: '/asignacion', label: 'Asignación', icon: 'mdi:account-arrow-right-outline', show: (s) => s.admin || s.supervisor || !!s.me?.inTriage },
  { to: '/directorio', label: 'Directorio', icon: 'mdi:account-group-outline' },
  { to: '/reportes', label: 'Reportes', icon: 'mdi:chart-bar', show: (s) => s.admin || s.supervisor || s.can('reports.read') },
  // Correos al ciudadano que no salieron: con el permiso «Ver correos no enviados» y el contador de pendientes.
  { to: '/correos-no-enviados', label: 'Correos no enviados', icon: 'mdi:email-alert-outline', show: (s) => s.can('emails.failed') },
  { to: '/configuracion', label: 'Configuración', icon: 'mdi:cog-outline', show: (s) => s.admin || s.can('templates.manage') },
];

/** Los enlaces del armazón son rutas de react-router. */
const Enlace = ({ href, className, children, onClick, ...rest }: EnlaceProps) => <Link to={href} className={className} onClick={onClick} aria-current={rest['aria-current']}>{children}</Link>;

/**
 * Armazón del SGDEA sobre `ShellApp` de `@xiltrion/ui`, el mismo de las apps de la suite: barra lateral con la
 * navegación, selector de apps (aparece cuando la entidad tenga más de una), avisos y menú de usuario. PQRS conserva
 * su buscador con el atajo «/» en la barra superior y la paleta de comandos con ⌘K (ver `useGlobalShortcuts`).
 */
export function AppShell({ children }: { children?: ReactNode }) {
  const session = useSession();
  const { me, askLogout } = session;
  const nav = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>(getThemeMode());
  const cycleTheme = () => { const next: ThemeMode = theme === 'light' ? 'dark' : 'light'; setThemeMode(next); setTheme(next); };
  useEffect(() => { const on = () => setTheme(getThemeMode()); window.addEventListener(THEME_EVENT, on); return () => window.removeEventListener(THEME_EVENT, on); }, []);
  // En puesta en marcha (`me.operation`, la misma marca de `/setup`) primero se termina eso: sin navegación, selector de
  // apps, buscador, paleta, atajos ni avisos; solo el asistente, las pantallas de configuración que abre y «Cerrar
  // sesión» (las rutas las cierra `RequireAuth`, y el API responde 409 `SETUP_PENDING` a la operación).
  const setup = me?.operation === 'SETUP';
  const handlers = useMemo(() => ({ openSearch: () => setSearchOpen(true), openPalette: () => setPaletteOpen(true) }), []);
  useGlobalShortcuts(handlers);
  // «Mis tareas» del menú cuenta todo lo que hay por hacer: las tareas abiertas y los apoyos por responder de sus áreas.
  const dash = useMyDashboard(!!me && !setup).data;
  const todo = (dash?.pending ?? 0) + (useSupportInboxCount(!!me && !setup).data?.pending ?? 0);
  // Término legal de los expedientes a su cargo: vencidos en rojo; si no hay, los que vencen hoy en ámbar.
  const termOverdue = dash?.term.overdue ?? 0;
  const termToday = dash?.term.dueToday ?? 0;
  const emailsFailed = useFailedEmailCount().data?.count ?? 0;
  const unread = useQuery({ queryKey: ['notifications', 'unread-count'], queryFn: () => apiGet<{ count: number }>('/notifications/unread-count'), refetchInterval: 30_000, enabled: !!me && !setup });
  const brand = useQuery({ ...publicBrandingQuery, enabled: !!me });
  // Con logo de la entidad, este va en la barra lateral en lugar de su nombre.
  const entityLogo = brand.data?.logo ?? null;
  const entityName = realEntityName(me?.entity.name);

  // Contadores del menú: tareas y apoyos por hacer, y correos al ciudadano que no salieron.
  const badges: Record<string, ReactNode> = {
    ...(termOverdue > 0 ? { '/pqrs': <span className="tnum text-xs font-semibold rounded-full bg-destructive text-destructive-foreground px-1.5">{termOverdue}<span className="sr-only"> expedientes vencidos</span></span> } : termToday > 0 ? { '/pqrs': <span className="tnum text-xs font-medium rounded-full bg-warning/30 px-1.5">{termToday}<span className="sr-only"> expedientes vencen hoy</span></span> } : {}),
    ...(todo > 0 ? { '/tareas': <span className="tnum text-xs font-medium rounded-full bg-warning/20 px-1.5">{todo}<span className="sr-only"> pendientes</span></span> } : {}),
    ...(emailsFailed > 0 ? { '/correos-no-enviados': <span className="tnum text-xs font-medium rounded-full bg-destructive/15 text-destructive px-1.5">{emailsFailed}<span className="sr-only"> pendientes</span></span> } : {}),
  };
  const navegacion: SeccionNavegacion[] = [{
    id: 'main',
    items: setup ? [] : NAV.filter((n) => !n.show || n.show(session)).map((n) => ({ id: n.to, etiqueta: n.label, icono: n.icon, href: n.to, insignia: badges[n.to] })),
  }];
  const self = { id: APP_CODE, nombre: 'SGDEA', icono: 'mdi:file-document-multiple-outline' as const, href: '/', actual: true };
  // El armazón ocupa la ventana y desplaza solo su área principal. Lo absoluto sin ancestro posicionado fuera de `#main`
  // (textos `sr-only`, ayudantes de los campos) se ubica respecto del documento y, si cae fuera de la ventana, la estira:
  // aparece un segundo scroll que mueve también la barra lateral y la superior. `relative` lo contiene aquí y
  // `overflow-clip` lo recorta sin volver esto un contenedor desplazable (un `scrollIntoView` tampoco lo puede mover).
  return (
    <div className="relative h-dvh overflow-clip">
      <ShellApp
        nombreApp="SGDEA"
        // La marca y el logo de Datalog (cabecera en puesta en marcha y pie de la barra lateral) son los del paquete: sin
        // `marca` ni `logoPie`, `ShellApp` usa `MarcaDatalog` y `LogoDatalog`, iguales en todas las apps y en el portal de la suite.
        nombreSuite="Suite Datalog"
        navegacion={navegacion}
        rutaActual={location.pathname}
        Enlace={Enlace}
        // Las apps de la suite habilitadas para la entidad (`/auth/me`, armadas con `appsForShell`); con una sola, el selector no aparece.
        apps={!setup && me?.suiteApps?.length ? me.suiteApps : [self]}
        // «Panel de control» al pie del lanzador: la entidad en la consola de la suite, solo para el Administrador (el rol
        // que la suite alinea con el ADMIN de la entidad, §3.9) y con la suite conectada.
        panelControl={!setup && me?.suite.url && me.roleCodes.includes(SystemRole.ADMIN) ? me.suite.url : undefined}
        entidad={entityName || entityLogo ? { nombre: entityName ?? '', logo: entityLogo ?? undefined } : undefined}
        usuario={{ nombre: me?.displayName ?? '', correo: me?.email ?? '' }}
        // Franja «Vista Superadmin»: el rol Super solo lo tiene el superadministrador de Datalog (`platform_role` =
        // `SUPERADMIN` en cada ingreso, `SuiteUsersService.alignPlatformRole`).
        vistaSuperadmin={!!me?.roleCodes.includes(SystemRole.SUPER)}
        // En puesta en marcha no hay a dónde navegar: sin barra lateral, solo la barra superior con la marca.
        sinBarraLateral={setup}
        version={__APP_VERSION__}
        barraSuperior={!setup ? (
          <Button variant="outline" size="sm" onClick={() => setSearchOpen(true)} className="w-72 max-w-full justify-start gap-2 px-3 font-normal text-muted-foreground" aria-label="Buscar (atajo /)">
            <Icono nombre="mdi:magnify" aria-hidden /> <span className="truncate">Buscar radicado, ciudadano, documento…</span> <Kbd className="ml-auto">/</Kbd>
          </Button>
        ) : undefined}
        avisos={!setup ? <NotificationsBell count={unread.data?.count ?? 0} /> : undefined}
        menuUsuario={[
          ...(!setup ? [{ etiqueta: 'Mi perfil', icono: 'mdi:account-circle-outline' as const, onSelect: () => nav('/perfil') }] : []),
          ...(!setup ? [{ etiqueta: 'Atajos de teclado', icono: 'mdi:keyboard-outline' as const, onSelect: () => setPaletteOpen(true) }] : []),
          { etiqueta: theme === 'light' ? 'Modo oscuro' : 'Modo claro', icono: theme === 'light' ? 'mdi:weather-night' : 'mdi:white-balance-sunny', onSelect: cycleTheme },
          { etiqueta: 'Cerrar sesión', icono: 'mdi:logout', destructivo: true, onSelect: askLogout },
        ]}
      >
        {/* `relative`: los ayudantes con posición absoluta de los campos (el input oculto de la carga de archivos, el select nativo de
            un selector) se posicionan dentro del área con scroll; sin esto quedan relativos al documento y, en una página larga,
            estiran la ventana y aparece un espacio vacío bajo la aplicación. */}
        {/* Una vista de alto fijo (el diseñador de flujos, `data-alto-pantalla`) ocupa lo que queda del área con `flex-1`:
            `min-h-full` no da un alto contra el cual resolver porcentajes, y la vista crecería con su contenido. */}
        <div id="main" className="relative min-h-full p-4 lg:p-6 has-[>[data-alto-pantalla]]:flex has-[>[data-alto-pantalla]]:flex-col">
          <SetupNotice />
          {!setup && <EmailFailuresNotice />}
          {children ?? <Outlet />}
        </div>
        {!setup && <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />}
        {!setup && <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />}
      </ShellApp>
    </div>
  );
}
